import moment from 'moment';
import React, { FC, useContext } from 'react';
import { renderedData } from '../../SearchFormComponent';
import { StyledFlexiCalendarBoxDiv } from './flexibleDateCalendar.styles';
import { FlexibleCalendarType } from './flexibleDateCalendar.types';
import { checkLocaleToAddYears } from '../../../utils/DateUtils';
import { useConditionalPageContextSERP } from '../../../../../../hooks';
import { ContentContext } from '../../index.contexts';
import { useConditionalSearchFormRenderedDataSERP } from '../../../../../../hooks/lib/useConditionalRenderedData';
import { getSearchFormClickTrackingLoc } from '../../../../../../modules/utils/searchFormHelper';

const FlexibleDateCalendar: FC<FlexibleCalendarType> = ({
  monthList,
  setDateHandler,
  singleDateLimit,
  useEnglishMomentForCalculation,
}) => {
  const { nightCount, setNightCount, monthSelected, setMonthSelected, yearSelected, setYearSelected } =
    useConditionalSearchFormRenderedDataSERP(renderedData);

  const { maxNumberOfNights, howManyNights, nightCountDecreaseLabel, nightCountIncreaseLabel } =
    useContext(ContentContext);

  const nightsLimit = maxNumberOfNights ? maxNumberOfNights : 9;
  let lastMonthToShow: number;
  let lastYeartoShow: number;
  const { currentLocale } = useConditionalPageContextSERP();
  let latestMonth: number;
  let currentYear: number;
  let nextYear: number;

  if (useEnglishMomentForCalculation) {
    latestMonth = Number(moment().locale('en').format('M')) - 1;
    currentYear = Number(moment().locale('en').format('YYYY'));
    nextYear = currentYear + 1;
    lastMonthToShow = Number(moment(singleDateLimit).locale('en').format('M')) - 1;
    lastYeartoShow = Number(moment(singleDateLimit).locale('en').format('YYYY'));
  } else {
    latestMonth = Number(moment().format('M')) - 1;
    currentYear = Number(moment().format('YYYY'));
    nextYear = currentYear + 1;
    lastMonthToShow = Number(moment(singleDateLimit).format('M')) - 1;
    lastYeartoShow = Number(moment(singleDateLimit).format('YYYY'));
  }

  const onMonthSelection = (monthInd: number, year: number): void => {
    setYearSelected(year);
    setMonthSelected(monthInd);
    if (setDateHandler) {
      setDateHandler(monthInd, nightCount, year);
    }
  };

  const onNightCountAdd = (): void => {
    const nightsCountInFlexibleSearch = nightCount < Number(nightsLimit) ? nightCount + 1 : Number(nightsLimit);
    setNightCount(nightsCountInFlexibleSearch);
    if (setDateHandler) {
      setDateHandler(monthSelected, nightsCountInFlexibleSearch, yearSelected);
    }
  };

  const onNightCountDecr = (): void => {
    const nightsCountInFlexibleSearch = nightCount <= 1 ? 1 : nightCount - 1;
    setNightCount(nightsCountInFlexibleSearch);
    if (setDateHandler) {
      setDateHandler(monthSelected, nightsCountInFlexibleSearch, yearSelected);
    }
  };
  const isDecrementDisabled = nightCount < 2;
  const isIncrementDisabled = nightCount > Number(nightsLimit) - 1;

  return (
    <StyledFlexiCalendarBoxDiv
      data-component-name="a-shop-FlexibleDateCalendar"
      data-testid="shop-FlexibleDateCalendar"
      className="search__flexible_calendar"
    >
      <div className="row mx-0 stepper-container">
        <div className="t-subtitle-m stepper-container__label">{howManyNights}</div>
        <div className="stepper-container__counter">
          <div>
            <button
              type="button"
              className={`${
                isDecrementDisabled ? 'disable-btn' : ''
              } custom_click_track stepper-container__counter__btn`}
              {...{
                custom_click_track_value: `${getSearchFormClickTrackingLoc()}| Flexible Date Picker Day minus button |internal`,
              }}
              aria-label={`${nightCountDecreaseLabel} ${isDecrementDisabled ? 'Disabled' : 'Enabled'}`}
              aria-disabled={isDecrementDisabled}
              disabled={isDecrementDisabled}
              tabIndex={isDecrementDisabled ? -1 : 0}
              onClick={onNightCountDecr}
              data-testid="decNightCount"
            >
              <span className="icon-stepper-minus t-title-s"></span>
            </button>
          </div>
          <div className="t-subtitle-xl" aria-live="polite">
            {nightCount}
          </div>
          <div>
            <button
              type="button"
              className={`${
                isIncrementDisabled ? 'disable-btn' : ''
              } custom_click_track stepper-container__counter__btn`}
              {...{
                custom_click_track_value: `${getSearchFormClickTrackingLoc()}| Flexible Date Picker Day addition button |internal`,
              }}
              aria-label={`${nightCountIncreaseLabel} ${isIncrementDisabled ? 'Disabled' : 'Enabled'}`}
              aria-disabled={isIncrementDisabled}
              disabled={isIncrementDisabled}
              tabIndex={isIncrementDisabled ? -1 : 0}
              onClick={onNightCountAdd}
              data-testid="incNightCount"
            >
              <span className="icon-stepper-plus t-title-s"></span>
            </button>
          </div>
        </div>
      </div>

      <div className="months-container">
        <div className="t-font-xs display-year">{checkLocaleToAddYears(currentLocale, currentYear)}</div>
        <div className="row mx-0">
          {monthList?.map((month, index) => {
            return (
              <div>
                {(Number(lastYeartoShow) === Number(currentYear)
                  ? index <= lastMonthToShow && index >= latestMonth
                  : index >= latestMonth) && (
                  <button
                    className={`custom_click_track m-button-secondary ${
                      monthSelected === index && yearSelected === currentYear ? 'selected-month' : ''
                    } months-cta`}
                    {...{
                      custom_click_track_value: `${getSearchFormClickTrackingLoc()}| Flexible Date Picker Month Selected ${month} button |internal`,
                    }}
                    type="button"
                    onClick={(): void => onMonthSelection(index, currentYear)}
                    data-testid="months-cta-test"
                  >
                    {monthSelected === index && yearSelected === currentYear && (
                      <span className="icon-check check-mark"></span>
                    )}
                    <span>{month}</span>
                  </button>
                )}
              </div>
            );
          })}
        </div>
        {nextYear <= lastYeartoShow && (
          <>
            <div className="t-font-xs display-year">
              {Number(checkLocaleToAddYears(currentLocale, currentYear) + 1)}
            </div>
            <div className="row mx-0">
              {monthList?.map((month, index) => {
                return (
                  <div>
                    {index <= lastMonthToShow && (
                      <button
                        className={`custom_click_track m-button-secondary ${
                          monthSelected === index && yearSelected === nextYear ? 'selected-month' : ''
                        } months-cta`}
                        {...{
                          custom_click_track_value: `${getSearchFormClickTrackingLoc()}| Flexible Date Picker Month Selected ${month} button |internal`,
                        }}
                        type="button"
                        onClick={(): void => onMonthSelection(index, nextYear)}
                      >
                        {monthSelected === index && yearSelected === nextYear && (
                          <span className="icon-check check-mark"></span>
                        )}
                        {month}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </StyledFlexiCalendarBoxDiv>
  );
};

export default FlexibleDateCalendar;
